import React, { FC, forwardRef } from "react";
import Box, { BoxProps } from "../box/Box";
import SvgBox from "../box/SvgBox";
import Input, { IInput } from "../input/Input";

type SelectAttributes = Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "size" | "disabled" | "required" | "defaultChecked"
>;

type MergeSelectAttributes = Omit<BoxProps, "ref" | "as" | "defaultChecked"> & SelectAttributes;

export type ISelect = IInput & MergeSelectAttributes & React.RefAttributes<HTMLSelectElement>;

export type SelectProps = Omit<ISelect, "width" | "height"> & {
  /**
   * The props passed to the select's root element.
   *
   * The internal structure looks like this:
   *
   * ```jsx
   * <SelectWrapper {...rootProps}>
   *  <Select /> <== most props go here directly
   *  <SelectIconWrapper />
   * </SelectWrapper>
   * ```
   *
   * In some scenario, you might want to pass some other props to the root.
   */
  rootProps?: BoxProps;
  /**
   * The placeholder for the select. This renders an `<option>` with empty value
   *
   * ```jsx
   * <option value="">{placeholder}</option>
   * ```
   */
  placeholder?: string;

  width?: any;

  height?: any;
};

const SelectIconWrapper: FC<BoxProps> = forwardRef(function SelectIconWrapper(props, ref) {
  return (
    <Box
      ref={ref}
      position="absolute"
      display="inline-flex"
      width="1.5rem"
      height="100%"
      alignItems="center"
      justifyContent="center"
      right="0.5rem"
      top="50%"
      pointerEvents="none"
      zIndex={2}
      transform="translateY(-50%)"
      {...props}
    />
  );
});

const SelectInput = forwardRef<HTMLSelectElement, SelectProps>(function SelectInput(
  { placeholder, children, ...rest },
  ref,
) {
  return (
    <Input as="select" appearance="none" ref={ref} pr="2rem" pb="px" lineHeight="normal" {...rest}>
      {placeholder && <option value="">{placeholder}</option>}
      {children}
    </Input>
  );
});

const Select: FC<SelectProps> = forwardRef(({ rootProps, ...props }, ref) => {
  const opacity = props.readOnly || props.disabled ? 0.5 : null;

  const [root, select] = splitProps(props);

  return (
    <Box position="relative" width="100%" {...root} {...rootProps}>
      <SelectInput ref={ref} color="inherit" {...select} />
      <SelectIconWrapper opacity={opacity} color={select.color || "inherit"}>
        <SvgBox viewBox="0 0 24 24" verticalAlign="middle" width={24} height={24}>
          <path fill="currentColor" d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
        </SvgBox>
      </SelectIconWrapper>
    </Box>
  );
});

const rootOptions = [
  "m",
  "mt",
  "mr",
  "mb",
  "ml",
  "mx",
  "my",
  "margin",
  "marginTop",
  "marginBottom",
  "marginLeft",
  "marginRight",
  "marginY",
  "marginX",
  "flex",
  "flexBasis",
  "width",
  "minWidth",
  "maxWidth",
  "maxW",
  "minW",
  "w",
  "zIndex",
  "top",
  "right",
  "bottom",
  "left",
  "position",
  "pos",
];

const splitProps = (props: SelectProps): any => {
  const rootProps: any = {};
  const selectProps: any = {};
  // eslint-disable-next-line
  for (const key in props) {
    if (rootOptions.includes(key)) {
      rootProps[key] = (props as any)[key];
    } else {
      selectProps[key] = (props as any)[key];
    }
  }
  return [rootProps, selectProps];
};

Select.displayName = "Select";

export default Select;
